import { useEffect, useRef } from "react";
import { ActivityIndicator, StyleSheet } from "react-native";
import { useJsApiLoader, GoogleMap, Marker } from "@react-google-maps/api";
import AppStore from "../../storage/appStore";
import { os } from "../../platform";
import { getApiKey } from "../../utils/api/appDataApi";
import { COLORS } from "../../theme/colors";
import { STRINGS } from "../../constants/strings";

const mapOptions = {
  styles: [
    {
      featureType: "poi",
      stylers: [{ visibility: "off" }],
    },
  ],
  disableDefaultUI: true,
  gestureHandling: "none",
  keyboardShortcuts: false,
};

const LoadingPlaceholder = () => (
  <div
    style={{
      ...styles.wrapper,
      ...styles.loadingIndicator,
      backgroundColor: COLORS.uiGray200,
    }}
  >
    <ActivityIndicator size="large" color="#999" />
  </div>
);

const MapContent = ({ lat, lng }) => {
  const webGoogleMapsApiKey = AppStore((state) => state.webGoogleMapsApiKey);
  const setWebGoogleMapsApiKey = AppStore((state) => state.setWebGoogleMapsApiKey);
  const hasRefreshed = useRef(false);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: webGoogleMapsApiKey,
  });

  useEffect(() => {
    if (loadError && !hasRefreshed.current) {
      hasRefreshed.current = true;
      // Key may be expired/rotated — clear it to trigger a re-fetch
      setWebGoogleMapsApiKey(null);
    }
  }, [loadError, setWebGoogleMapsApiKey]);

  if (loadError && hasRefreshed.current) {
    return <LoadingPlaceholder />;
  }

  if (!isLoaded) return <LoadingPlaceholder />;

  return (
    <GoogleMap mapContainerStyle={styles.wrapper} center={{ lat, lng }} zoom={19} options={mapOptions}>
      {lat && lng && <Marker position={{ lat, lng }} />}
    </GoogleMap>
  );
};

const PropertyMap = ({ lat, lng }) => {
  const webGoogleMapsApiKey = AppStore((state) => state.webGoogleMapsApiKey);
  const setWebGoogleMapsApiKey = AppStore((state) => state.setWebGoogleMapsApiKey);
  const setErrorMessage = AppStore((state) => state.setErrorMessage);

  useEffect(() => {
    const fetchWebGoogleMapKey = async () => {
      if (os === "web" && webGoogleMapsApiKey === null) {
        try {
          const response = await getApiKey("googleMaps");
          setWebGoogleMapsApiKey(response.key);
        } catch (error) {
          setErrorMessage(STRINGS.failedToLoadData);
        }
      }
    };

    fetchWebGoogleMapKey();
  }, [webGoogleMapsApiKey, setWebGoogleMapsApiKey, setErrorMessage]);

  return (
    <div style={styles.container}>
      {!webGoogleMapsApiKey ? <LoadingPlaceholder /> : <MapContent lat={lat} lng={lng} />}
    </div>
  );
};

const styles = StyleSheet.create({
  container: {
    display: "flex",
    justifyContent: "center",
    marginBottom: 3,
  },
  wrapper: {
    height: 200,
    width: 200,
    overflow: "hidden",
    borderRadius: 999,
    border: "4px solid white",
    boxShadow: "0 0 0 2px #e5e8eeff", // spread = border + gap
  },
  loadingIndicator: {
    display: "flex",
    justifyContent: "center",
  },
});

export default PropertyMap;
